import type { InsightsCompactFilters } from '@/common/api/types/dashboard/filters';
import { fromDateString } from '@/common/utils/dateFormats';
import type {
  BooleanOperator,
  DateOperator,
  PicklistOperator,
} from '@/common/zod-schemas/common/operators';
import type { ItemFilterOperator } from '@/common/zod-schemas/itemFilterSearchSchema';
import {
  isBooleanFilterKey,
  isCodeFilterKey,
  isDateFilterKey,
  isPicklistFilterKey,
} from './filterKeyGuards';
import type { CompactFilterKey } from './filterKeyTypes';
import {
  booleanOperatorToUrl,
  CODE_FILTER_REGEX,
  CODE_VALUE_REGEX,
  dateOperatorToUrl,
  isBooleanOperatorValid,
  isDateOperatorValid,
  isPicklistOperatorValid,
  isValidBooleanMatch,
  isValidDateMatch,
  isValidPicklistMatch,
  picklistOperatorToUrl,
  urlToBooleanOperator,
  urlToDateOperator,
  urlToPicklistOperator,
} from './filterOperators';

export type ParsedListFilter<T> = {
  value: T[] | null;
  operator: PicklistOperator;
};

export type ParsedPicklistFilter = ParsedListFilter<number>;

export type ParsedCodeFilter = ParsedListFilter<string>;

export type ParsedDateFilter = {
  value: string[] | null;
  operator: DateOperator;
};

export type ParsedBooleanFilter = {
  value: boolean | null;
  operator: BooleanOperator;
};

const transformCompactToListFilter = <T>(
  compactString: string | undefined,
  parseValues: (valueStr: string) => T[],
): ParsedListFilter<T> | undefined => {
  if (!compactString) return undefined;

  if (compactString === 'empty' || compactString === 'notempty') {
    return { operator: urlToPicklistOperator[compactString], value: null };
  }

  if (compactString === 'in') {
    return { operator: urlToPicklistOperator[compactString], value: [] };
  }

  const match = compactString.match(/^(in|empty|notempty)_(.*)$/);
  if (!isValidPicklistMatch(match)) return undefined;

  const [, urlOp, valueStr = ''] = match;

  if (urlOp === 'empty' || urlOp === 'notempty') {
    return { operator: urlToPicklistOperator[urlOp], value: null };
  }

  if (valueStr !== valueStr.trim() || valueStr.trim() === '' || valueStr.includes(' ')) {
    return undefined;
  }

  return { operator: urlToPicklistOperator[urlOp], value: parseValues(valueStr) };
};

export const transformCompactToPicklistFilter = (
  compactString: string | undefined,
): ParsedPicklistFilter | undefined =>
  transformCompactToListFilter(compactString, (valueStr) =>
    valueStr.includes('-') ? valueStr.split('-').map(Number) : [Number(valueStr)],
  );

export const transformCompactToCodeFilter = (
  compactString: string | undefined,
): ParsedCodeFilter | undefined =>
  transformCompactToListFilter(compactString, (valueStr) => valueStr.split(','));

const BARE_IN_OPERATOR = 'in';

const transformListFilterToCompact = (
  values: (string | number | boolean)[],
  operator: ItemFilterOperator | BooleanOperator,
  separator: string,
): string => {
  if (!isPicklistOperatorValid(operator)) return '';

  const urlOp = picklistOperatorToUrl[operator];

  if (operator === 'null' || operator === 'notnull' || values.length === 0) {
    return urlOp;
  }

  return `${urlOp}_${values.join(separator)}`;
};

export const transformCodeFilterToCompact = (
  values: (string | number | boolean)[],
  operator: ItemFilterOperator | BooleanOperator,
): string => {
  const encodable = values.filter((value) => CODE_VALUE_REGEX.test(String(value)));
  const compact = transformListFilterToCompact(encodable, operator, ',');

  return CODE_FILTER_REGEX.test(compact) ? compact : BARE_IN_OPERATOR;
};

export const transformCompactToDateFilter = (
  compactString: string | undefined,
): ParsedDateFilter | undefined => {
  if (!compactString || compactString === '') return undefined;

  if (compactString === 'empty' || compactString === 'notempty') {
    return { operator: urlToDateOperator[compactString], value: null };
  }

  if (
    compactString === 'is' ||
    compactString === 'after' ||
    compactString === 'onOrAfter' ||
    compactString === 'before' ||
    compactString === 'onOrBefore' ||
    compactString === 'in' ||
    compactString === 'isNot'
  ) {
    return { operator: urlToDateOperator[compactString], value: null };
  }

  const match = compactString.match(/^(is|after|onOrAfter|before|onOrBefore|in|isNot)_(.*)$/);

  if (isValidDateMatch(match)) {
    const [, urlOp, valueStr = ''] = match;

    if (!valueStr || valueStr.trim() === '') {
      return undefined;
    }

    if (valueStr !== valueStr.trim() || valueStr.includes(' ')) {
      return undefined;
    }

    const values = valueStr.includes('_') ? valueStr.split('_') : [valueStr];

    // Parse first date (fromDateString already returns UTC midnight)
    const firstDate = fromDateString(values[0]);
    if (!firstDate) return undefined;

    const dateOperator = urlToDateOperator[urlOp];

    // Handle single-date operators (return as array for consistency with UI)
    if (
      values.length === 1 &&
      ['is', 'after', 'onOrAfter', 'before', 'onOrBefore', 'isNot'].includes(urlOp)
    ) {
      return {
        operator: dateOperator,
        value: [firstDate.toISOString()],
      };
    }

    if (values.length === 2 && urlOp === 'in') {
      const secondDate = fromDateString(values[1]);
      if (!secondDate) return undefined;
      return {
        operator: dateOperator,
        value: [firstDate.toISOString(), secondDate.toISOString()],
      };
    }

    return undefined;
  }

  return undefined;
};

export const transformCompactToBooleanFilter = (
  compactString: string | undefined,
): ParsedBooleanFilter | undefined => {
  if (!compactString || compactString === '') return undefined;

  if (compactString === 'empty' || compactString === 'notempty') {
    return { operator: urlToBooleanOperator[compactString], value: null };
  }

  if (compactString === 'is') {
    return { operator: urlToBooleanOperator[compactString], value: true };
  }

  const match = compactString.match(/^(is|empty|notempty)_(.*)$/);

  if (isValidBooleanMatch(match)) {
    const [, urlOp, valueStr = ''] = match;

    if (urlOp === 'empty' || urlOp === 'notempty') {
      return { operator: urlToBooleanOperator[urlOp], value: null };
    }

    if (!valueStr || valueStr.trim() === '') {
      return undefined;
    }

    if (valueStr !== valueStr.trim() || valueStr.includes(' ')) {
      return undefined;
    }

    if (valueStr === 'true' || valueStr === 'false') {
      return {
        operator: urlToBooleanOperator[urlOp],
        value: valueStr === 'true',
      };
    }

    return undefined;
  }

  return undefined;
};

export const transformFilterToCompact = (
  values: (string | number | boolean)[],
  operator: ItemFilterOperator | BooleanOperator,
): string => {
  const isDateOperator = isDateOperatorValid(operator);
  const isBooleanOperatorType = isBooleanOperatorValid(operator);
  const isDateValue =
    values.length > 0 ? typeof values[0] === 'string' && values[0].includes('-') : isDateOperator;
  const isBooleanValue = values.length > 0 ? typeof values[0] === 'boolean' : isBooleanOperatorType;

  if (operator === 'null' || operator === 'notnull') {
    if (isDateOperator) return dateOperatorToUrl[operator];
    if (isBooleanOperatorType) return booleanOperatorToUrl[operator];
    return picklistOperatorToUrl[operator];
  }

  if (isBooleanValue && isBooleanOperatorValid(operator)) {
    if (values.length === 0) {
      return booleanOperatorToUrl[operator];
    }

    const valueStr = String(values[0]);
    const urlOp = booleanOperatorToUrl[operator];
    return `${urlOp}_${valueStr}`;
  }

  if (isDateValue && isDateOperatorValid(operator)) {
    if (values.length === 0) {
      return dateOperatorToUrl[operator];
    }

    const dateOnlyValues = (values as string[]).map((v) => {
      const date = fromDateString(v);
      if (!date) return v.split('T')[0];
      return date.toISOString().split('T')[0];
    });

    const valueStr = operator === 'eq' ? dateOnlyValues[0] : dateOnlyValues.join('_');
    const urlOp = dateOperatorToUrl[operator];
    return `${urlOp}_${valueStr}`;
  }

  if (isPicklistOperatorValid(operator)) {
    if (values.length === 0) {
      return picklistOperatorToUrl[operator];
    }

    const valueStr = Array.isArray(values) ? values.join('-') : String(values);
    const urlOp = picklistOperatorToUrl[operator];
    return `${urlOp}_${valueStr}`;
  }

  return '';
};

export const transformCompactToFilterByKey = (key: CompactFilterKey, value: string | undefined) => {
  if (isPicklistFilterKey(key)) {
    return transformCompactToPicklistFilter(value);
  }
  if (isCodeFilterKey(key)) {
    return transformCompactToCodeFilter(value);
  }
  if (isDateFilterKey(key)) {
    return transformCompactToDateFilter(value);
  }
  if (isBooleanFilterKey(key)) {
    return transformCompactToBooleanFilter(value);
  }
  return undefined;
};

export const transformFilterToCompactByKey = (
  key: CompactFilterKey,
  values: (string | number | boolean)[],
  operator: ItemFilterOperator | BooleanOperator,
): string =>
  isCodeFilterKey(key)
    ? transformCodeFilterToCompact(values, operator)
    : transformFilterToCompact(values, operator);

export const buildInsightsStructuredSearch = (filters: InsightsCompactFilters) => {
  return {
    scopeItemId: filters.scopeItemId,
    teamId: transformCompactToPicklistFilter(filters.teamId),
    statusId: transformCompactToPicklistFilter(filters.statusId),
    itemTypeId: transformCompactToPicklistFilter(filters.itemTypeId),
    plannedStartDateTime: transformCompactToDateFilter(filters.plannedStartDateTime),
    plannedFinishDateTime: transformCompactToDateFilter(filters.plannedFinishDateTime),
  };
};

export type TeamMembershipCompactFilters = {
  locationId?: string;
  roleId?: string;
  showDeactivatedMembers?: string;
  skillId?: string;
  holidaySchemeId?: string;
  workloadSchemeId?: string;
  loopId?: string;
  employmentType?: string;
};

export const buildResourceStructuredSearch = (filters: TeamMembershipCompactFilters) => {
  return {
    locationId: transformCompactToPicklistFilter(filters.locationId),
    roleId: transformCompactToPicklistFilter(filters.roleId),
    showDeactivatedMembers: transformCompactToBooleanFilter(filters.showDeactivatedMembers),
    skillId: transformCompactToPicklistFilter(filters.skillId),
    holidaySchemeId: transformCompactToPicklistFilter(filters.holidaySchemeId),
    workloadSchemeId: transformCompactToPicklistFilter(filters.workloadSchemeId),
    loopId: transformCompactToPicklistFilter(filters.loopId),
    employmentType: transformCompactToPicklistFilter(filters.employmentType),
  };
};

export type TeamCompactFilters = {
  roleId?: string;
  skillId?: string;
  locationId?: string;
  employmentType?: string;
  showDeactivatedMembers?: string;
  showPastMemberships?: string;
  showFutureMemberships?: string;
};

export const buildTeamStructuredSearch = (filters: TeamCompactFilters) => {
  const parsedShowFutureMemberships = transformCompactToBooleanFilter(
    filters.showFutureMemberships,
  );
  const showFutureMembershipsValue =
    filters.showFutureMemberships === undefined || parsedShowFutureMemberships === undefined
      ? { operator: 'eq' as const, value: true }
      : parsedShowFutureMemberships;

  return {
    roleId: transformCompactToPicklistFilter(filters.roleId),
    skillId: transformCompactToPicklistFilter(filters.skillId),
    locationId: transformCompactToPicklistFilter(filters.locationId),
    employmentType: transformCompactToPicklistFilter(filters.employmentType),
    showDeactivatedMembers: transformCompactToBooleanFilter(filters.showDeactivatedMembers),
    showPastMemberships: transformCompactToBooleanFilter(filters.showPastMemberships),
    showFutureMemberships: showFutureMembershipsValue,
  };
};

export type TeamsCompactFilters = {
  roleId?: string;
  loopId?: string;
  skillId?: string;
  sourceType?: string;
};

export const buildTeamsStructuredSearch = (filters: TeamsCompactFilters) => {
  return {
    roleId: transformCompactToPicklistFilter(filters.roleId),
    loopId: transformCompactToPicklistFilter(filters.loopId),
    skillId: transformCompactToPicklistFilter(filters.skillId),
    sourceType: transformCompactToPicklistFilter(filters.sourceType),
  };
};

export type LoopTeamCompactFilters = {
  roleId?: string;
  skillId?: string;
  capacityCondition?: string;
  openRoleId?: string;
  openSkillId?: string;
};

export const buildLoopTeamStructuredSearch = (filters: LoopTeamCompactFilters) => {
  return {
    roleId: transformCompactToPicklistFilter(filters.roleId),
    skillId: transformCompactToPicklistFilter(filters.skillId),
    capacityCondition: transformCompactToPicklistFilter(filters.capacityCondition),
    openRoleId: transformCompactToPicklistFilter(filters.openRoleId),
    openSkillId: transformCompactToPicklistFilter(filters.openSkillId),
  };
};

export type LocationCompactFilters = {
  holidaySchemeId?: string;
  countryCode?: string;
  subdivisionCode?: string;
  showArchivedLocations?: string;
};

export const buildLocationStructuredSearch = (filters: LocationCompactFilters) => {
  return {
    holidaySchemeId: transformCompactToPicklistFilter(filters.holidaySchemeId),
    countryCode: transformCompactToCodeFilter(filters.countryCode),
    subdivisionCode: transformCompactToCodeFilter(filters.subdivisionCode),
    showArchivedLocations: transformCompactToBooleanFilter(filters.showArchivedLocations),
  };
};

export type SkillCompactFilters = {
  showCriticalSkills?: string;
};

export const buildSkillStructuredSearch = (filters: SkillCompactFilters) => {
  return {
    showCriticalSkills: transformCompactToBooleanFilter(filters.showCriticalSkills),
  };
};

export type OpenPositionCompactFilters = {
  locationId?: string;
  roleId?: string;
  skillId?: string;
  teamId?: string;
  showArchived?: string;
};

export const buildOpenPositionStructuredSearch = (filters: OpenPositionCompactFilters) => {
  return {
    locationId: transformCompactToPicklistFilter(filters.locationId),
    roleId: transformCompactToPicklistFilter(filters.roleId),
    skillId: transformCompactToPicklistFilter(filters.skillId),
    teamId: transformCompactToPicklistFilter(filters.teamId),
    showArchived: transformCompactToBooleanFilter(filters.showArchived),
  };
};

export type CapacitySchemeCompactFilters = {
  showArchived?: string;
};

export const buildCapacitySchemeStructuredSearch = (filters: CapacitySchemeCompactFilters) => {
  return {
    showArchived: transformCompactToBooleanFilter(filters.showArchived),
  };
};
